import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RASHI_NAMES } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { Term } from '../../components/Term';

/**
 * "How to read this kundli" — one collapsible reference card, drawn per style.
 *
 * Each guide is a SCHEMATIC, never the reader's data: where bhava 1 sits,
 * which way counting runs, what stays fixed and what moves, and how the lagna
 * is shown. The diagrams deliberately mirror the geometry of the real chart
 * components (NorthIndianChart / SouthIndianChart / EastIndianChart) so the
 * guide teaches the exact drawing on screen, not a textbook variant of it.
 *
 * Two ways in:
 *   - the toggle button beside the style switcher on ChartsScreen;
 *   - any link ending in `#kundli-guide` (the chart cards' "how do I read
 *     this?" affordance) — the hash opens the guide and scrolls to it, so the
 *     card is collapsible without ever being unfindable.
 *
 * Both directions of the Learn tie-up: every guide ends in "Learn this
 * properly →", pointing at the Learn lesson that teaches the three styles.
 */

/** Stable anchor id. Chart cards link to `/charts#${KUNDLI_GUIDE_ANCHOR}`. */
export const KUNDLI_GUIDE_ANCHOR = 'kundli-guide';

/**
 * The last history entry whose #kundli-guide was acted on. Module scope on
 * purpose — it has to outlive the component, which unmounts every time the
 * user switches to the Western tab. See the effect that reads it.
 */
let lastConsumedNavKey: string | null = null;

/** Test-only: module state leaks between cases in the same file. */
export function resetKundliGuideNavKey(): void {
  lastConsumedNavKey = null;
}

/**
 * The Learn lesson covering all three drawing styles
 * (`lagna-and-reading-the-diagram`, module "Your first kundali"), served on
 * App.tsx's `/learn/:lessonId` route — lesson ids are globally unique.
 */
const STYLE_LESSON_PATH = '/learn/lagna-and-reading-the-diagram';

type JyotishStyle = 'north' | 'south' | 'east';

/* --- Schematic geometry ----------------------------------------------------

   All three diagrams live in a 170×170 viewBox. The South and East styles
   share one fixed 4×4 ring of rashis (as the app's own chart components do);
   the North is the diamond. Colours are palette vars only: text in the
   -faint/-ink variants, fills/strokes in the non-ink tokens. */

const CELL = 36;
const GRID = 13;
const at = (i: number) => GRID + i * CELL;

/** sign index → [col, row] — the fixed ring both grid styles use. */
const GRID_CELLS: [number, number, number][] = [
  [11, 0, 0], [0, 1, 0], [1, 2, 0], [2, 3, 0],
  [3, 3, 1], [4, 3, 2], [5, 3, 3],
  [6, 2, 3], [7, 1, 3], [8, 0, 3],
  [9, 0, 2], [10, 0, 1],
];

/** Bhava-number anchors on the North diamond, houses 1..12. */
const NORTH_NUM: [number, number][] = [
  [85, 56], [46, 30], [30, 46], [56, 85], [30, 124], [46, 141],
  [85, 118], [124, 141], [140, 124], [118, 85], [140, 46], [124, 30],
];

function Arrowhead({ id }: { id: string }) {
  return (
    <marker id={id} viewBox="0 0 8 8" refX="6" refY="4" markerWidth="5" markerHeight="5" orient="auto">
      <path d="M 0 0 L 8 4 L 0 8 Z" fill="var(--text-faint)" />
    </marker>
  );
}

const DIAGRAM_STYLE = { width: '100%', maxWidth: 240, height: 'auto', display: 'block', margin: '0 auto' } as const;

function NorthDiagram() {
  return (
    <svg
      viewBox="0 0 170 170"
      role="img"
      aria-label="North Indian kundli schematic — fixed bhavas in a diamond, bhava 1 at the top centre, sign numbers rotating with the Lagna"
      style={DIAGRAM_STYLE}
    >
      <defs>
        <Arrowhead id="sg-arr-north" />
      </defs>
      <rect x="10" y="10" width="150" height="150" rx="4" fill="none" stroke="var(--border-strong)" strokeWidth="1.5" />
      <line x1="10" y1="10" x2="160" y2="160" stroke="var(--border-strong)" strokeWidth="1" />
      <line x1="160" y1="10" x2="10" y2="160" stroke="var(--border-strong)" strokeWidth="1" />
      <path d="M 85 10 L 160 85 L 85 160 L 10 85 Z" fill="none" stroke="var(--border-strong)" strokeWidth="1" />
      {NORTH_NUM.map(([x, y], i) => (
        <text
          key={i}
          x={x}
          y={y}
          textAnchor="middle"
          fontSize="10"
          fontWeight={i === 0 ? 700 : 400}
          fill={i === 0 ? 'var(--gold-ink)' : 'var(--text-faint)'}
        >
          {i + 1}
        </text>
      ))}
      <text x="85" y="34" textAnchor="middle" fontSize="8" fontWeight="700" fill="var(--gold-ink)">
        Lagna
      </text>
      <text x="85" y="70" textAnchor="middle" fontSize="7" fill="var(--text-faint)">
        your sign no. here
      </text>
      {/* Counting flows 1 → 2 → 3, anticlockwise around the top-left. */}
      <path d="M 64 4 Q 4 4 4 64" fill="none" stroke="var(--text-faint)" strokeWidth="1.2" markerEnd="url(#sg-arr-north)" />
    </svg>
  );
}

function SouthDiagram() {
  return (
    <svg
      viewBox="0 0 170 170"
      role="img"
      aria-label="South Indian kundli schematic — rashis fixed in a four-by-four grid, the Lagna cell marked with a corner stroke, bhavas counted clockwise"
      style={DIAGRAM_STYLE}
    >
      <defs>
        <Arrowhead id="sg-arr-south" />
      </defs>
      {GRID_CELLS.map(([sign, col, row]) => (
        <g key={sign}>
          <rect x={at(col)} y={at(row)} width={CELL} height={CELL} fill="none" stroke="var(--border-strong)" strokeWidth="1" />
          <text x={at(col) + CELL / 2} y={at(row) + 14} textAnchor="middle" fontSize="7.5" fill="var(--text-faint)">
            {RASHI_NAMES[sign].slice(0, 4)}
          </text>
        </g>
      ))}
      {/* An example lagna — Simha here — marked the conventional way. */}
      <line x1={at(3)} y1={at(2) + 10} x2={at(3) + 10} y2={at(2)} stroke="var(--gold)" strokeWidth="1.5" />
      <text x={at(3) + 7} y={at(2) + 30} fontSize="8" fontWeight="700" fill="var(--gold-ink)">
        La
      </text>
      {/* Bhavas are read clockwise from the marked cell. */}
      <path d="M 85 63 A 22 22 0 1 1 63 85" fill="none" stroke="var(--text-faint)" strokeWidth="1.2" markerEnd="url(#sg-arr-south)" />
    </svg>
  );
}

function EastDiagram() {
  return (
    <svg
      viewBox="0 0 170 170"
      role="img"
      aria-label="East Indian kundli schematic — rashis fixed in a square whose corner cells are split by diagonals, the Lagna cell shaded"
      style={DIAGRAM_STYLE}
    >
      {GRID_CELLS.map(([sign, col, row]) => {
        const x = at(col);
        const y = at(row);
        const isCorner = (col === 0 || col === 3) && (row === 0 || row === 3);
        // Same corner-diagonal directions as the real EastIndianChart.
        const diag = !isCorner
          ? null
          : (col === 0) === (row === 0)
            ? `M ${x} ${y + CELL} L ${x + CELL} ${y}`
            : `M ${x} ${y} L ${x + CELL} ${y + CELL}`;
        const isLagna = sign === 0; // example lagna: Mesha, shaded as the app shades it
        return (
          <g key={sign}>
            <rect
              x={x}
              y={y}
              width={CELL}
              height={CELL}
              fill={isLagna ? 'var(--gold-soft)' : 'none'}
              stroke="var(--border-strong)"
              strokeWidth="1"
            />
            {diag && <path d={diag} stroke="var(--border)" strokeWidth="1" fill="none" />}
            <text x={x + CELL / 2} y={y + 14} textAnchor="middle" fontSize="7.5" fill="var(--text-faint)">
              {RASHI_NAMES[sign].slice(0, 4)}
            </text>
            {isLagna && (
              <text x={x + 5} y={y + 30} fontSize="8" fontWeight="700" fill="var(--gold-ink)">
                La
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/* --- The guide itself ------------------------------------------------------ */

function GuideBody({ style }: { style: JyotishStyle }) {
  const { t } = useI18n();
  return (
    <div className="col" style={{ gap: 10, marginTop: 10 }}>
      <div className="tiny muted" style={{ lineHeight: 1.55 }}>
        India draws the same <Term id="kundali">kundli</Term> three ways. Only the drawing changes —
        the <Term id="graha">grahas</Term>, <Term id="rashi">rashis</Term> and{' '}
        <Term id="bhava">bhavas</Term> underneath are identical.
      </div>

      {style === 'north' && (
        <>
          <div className="h3">
            <Term id="north-indian-chart">North Indian</Term> — the diamond
          </div>
          <NorthDiagram />
          <div className="col tiny muted" style={{ gap: 6, lineHeight: 1.55 }}>
            <div>
              The bhavas never move: <Term id="bhava.1">bhava 1</Term> is always the diamond at the
              top centre, and the count runs anticlockwise — 2 sits to its upper left.
            </div>
            <div>
              What rotates are the sign numbers. Whichever rashi your <Term id="lagna">Lagna</Term>{' '}
              occupies, its number is written in bhava 1 and the other eleven follow in order.
            </div>
            <div>So this style needs no separate Lagna marker — the top diamond is the Lagna, every time.</div>
          </div>
        </>
      )}

      {style === 'south' && (
        <>
          <div className="h3">
            <Term id="south-indian-chart">South Indian</Term> — the fixed grid
          </div>
          <SouthDiagram />
          <div className="col tiny muted" style={{ gap: 6, lineHeight: 1.55 }}>
            <div>
              Every cell is permanently one rashi: <Term id="rashi.meena">Meena</Term> holds the
              top-left corner, <Term id="rashi.mesha">Mesha</Term> sits beside it, and the ring runs
              clockwise — the same in every kundli you will ever see.
            </div>
            <div>
              Your <Term id="lagna">Lagna</Term> is marked inside its rashi&apos;s cell with the small
              corner stroke. That cell becomes bhava 1, and bhavas are counted clockwise from it.
            </div>
            <div>
              Grahas are written inside the rashi they occupy — so two kundlis differ only in where
              the marks fall, never in where the rashis sit.
            </div>
          </div>
        </>
      )}

      {style === 'east' && (
        <>
          <div className="h3">
            <Term id="east-indian-chart">East Indian</Term> — the Bengali square
          </div>
          <EastDiagram />
          <div className="col tiny muted" style={{ gap: 6, lineHeight: 1.55 }}>
            <div>
              The rashis are fixed here too, in the same ring as the South grid — what changes is the
              drawing: the corner cells are split by diagonals, the shape charts take in Bengal,
              Odisha and Assam.
            </div>
            <div>
              Your <Term id="lagna">Lagna</Term>&apos;s cell is shaded and labelled La. It is bhava 1,
              and the bhavas follow the rashi ring from there.
            </div>
          </div>
        </>
      )}

      <Link
        to={STYLE_LESSON_PATH}
        className="small"
        style={{ color: 'var(--accent-ink)', fontWeight: 600 }}
      >
        {t('kundli.guide.learn')} →
      </Link>
    </div>
  );
}

export function ChartStyleGuide({
  style,
  defaultOpen,
}: {
  style: JyotishStyle;
  defaultOpen?: boolean;
}) {
  const { t } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(defaultOpen ?? false);
  const rootRef = useRef<HTMLDivElement>(null);

  /**
   * Router-driven arrivals: a <Link to="/charts#kundli-guide"> (or landing on
   * that hash) opens the guide and brings it on screen.
   *
   * KEYED ON location.key, NOT location.hash, and the consumed key is tracked
   * OUTSIDE the component. Both halves are load-bearing:
   *
   *  - On the hash alone, a second tap of "How to read this →" while the URL
   *    already carried that hash was a DEAD CONTROL. React Router pushes a new
   *    history entry (new `key`) but the hash string is unchanged, so the
   *    effect never re-ran, and a user who had collapsed the guide could tap
   *    the link forever with nothing happening. `key` changes on every
   *    navigation, so a repeat tap re-opens and re-scrolls.
   *
   *  - With component-local state, that fix would trade one bug for another.
   *    This guide only renders on the Jyotish tab, so flipping to Western and
   *    back REMOUNTS it — a fresh instance, stale hash still in the URL, and
   *    the page would yank itself down to the guide every single time. A
   *    module-level record of the consumed key survives the remount, so a
   *    navigation is honoured exactly once no matter how often the component
   *    is torn down.
   */
  useEffect(() => {
    if (location.hash !== `#${KUNDLI_GUIDE_ANCHOR}`) return;
    if (lastConsumedNavKey === location.key) return;
    lastConsumedNavKey = location.key;
    setOpen(true);
    // Optional-called: jsdom has no layout and no scrollIntoView.
    rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [location.key, location.hash]);

  // Plain <a href="#kundli-guide"> anchors bypass the router; catch those too.
  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === `#${KUNDLI_GUIDE_ANCHOR}`) setOpen(true);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return (
    <div className="card" id={KUNDLI_GUIDE_ANCHOR} ref={rootRef} style={{ padding: 8 }}>
      <button
        type="button"
        className="btn btn--ghost btn--block"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {t('kundli.guide.toggle')} {open ? '▴' : '▾'}
      </button>
      {open && <GuideBody style={style} />}
    </div>
  );
}
